import React, { useEffect, useState } from "react";
import { Newsletter, Meta } from "@/lib/api";
import { markNewsletterSeen } from "@/lib/useExitIntent";
import { getAttribution } from "@/lib/attribution";
import { Check, ArrowRight, AlertCircle } from "lucide-react";

// Stable fallback so Step 02 always has a working community link, even if
// /api/meta is slow, fails, or hasn't resolved yet. The settings document can
// still override this at runtime.
const FALLBACK_TYPEFORM_URL =
    "https://form.typeform.com/to/GQB5pKyv?typeform-source=frontier.m1c.vc";

/**
 * Reusable two-step newsletter signup widget.
 * Used in both <Hero /> (inline) and <NewsletterModal /> (overlay).
 */
export default function NewsletterForm({
    onClose,
    autoFocus = false,
    bg = "white",
}) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [alreadySubscribed, setAlreadySubscribed] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [typeformUrl, setTypeformUrl] = useState(FALLBACK_TYPEFORM_URL);

    useEffect(() => {
        Meta.get()
            .then((m) => {
                if (m?.community_typeform_url) {
                    setTypeformUrl(m.community_typeform_url);
                }
            })
            .catch(() => {
                /* keep fallback */
            });
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        setErrorMsg("");
        try {
            const res = await Newsletter.subscribe({
                email,
                source: onClose ? "modal" : "hero",
                ...getAttribution(),
            });
            setAlreadySubscribed(!!res.already);
            markNewsletterSeen();
            setStep(2);
        } catch {
            // Network error or server hiccup. The request may have actually
            // succeeded server-side, so don't claim outright failure.
            setErrorMsg(
                "Hmm, we couldn't confirm your subscription. If you don't get a confirmation email, please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    // Append the subscriber's email to the Typeform URL so the community
    // application pre-fills it. Requires a hidden field named "email" in Typeform.
    const buildCommunityUrl = () => {
        if (!typeformUrl) return "";
        try {
            const u = new URL(typeformUrl);
            if (email) u.searchParams.set("email", email);
            return u.toString();
        } catch {
            return typeformUrl;
        }
    };

    if (step === 1) {
        return (
            <div data-testid="newsletter-form">
                <div className="overline-cobalt mb-4">[Step 01] Subscribe</div>
                <h3
                    className="font-display mb-3"
                    style={{
                        fontSize: 30,
                        lineHeight: 1.05,
                        color: "var(--ink)",
                        letterSpacing: "-0.025em",
                    }}
                >
                    Join the{" "}
                    <span
                        style={{
                            fontFamily: "'Fraunces', serif",
                            fontStyle: "italic",
                            fontWeight: 400,
                            color: "var(--cobalt)",
                            marginLeft: "0.15em",
                        }}
                    >
                        Newsletter.
                    </span>
                </h3>
                <p
                    className="mb-5"
                    style={{
                        color: "var(--ink-2)",
                        fontSize: 13.5,
                        lineHeight: 1.6,
                    }}
                >
                    A once-a-week brief on the companies, capital, and technical
                    bets shaping the next decade. No filler.
                </p>
                <form onSubmit={submit}>
                    <input
                        type="email"
                        placeholder="you@work.email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoFocus={autoFocus}
                        className="input-tech mb-3"
                        style={{ background: bg === "white" ? "var(--panel-2)" : "white" }}
                        data-testid="newsletter-email-input"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full justify-center"
                        data-testid="newsletter-submit"
                    >
                        {loading ? "Subscribing…" : "Join The Newsletter →"}
                    </button>
                </form>
                {errorMsg ? (
                    <div
                        className="flex items-start gap-2 mt-3 p-3"
                        style={{
                            background: "rgba(255, 68, 0, 0.08)",
                            border: "1px solid rgba(255, 68, 0, 0.4)",
                            color: "var(--ink)",
                            fontSize: 12.5,
                            lineHeight: 1.5,
                        }}
                        data-testid="newsletter-error"
                        role="alert"
                    >
                        <AlertCircle
                            size={14}
                            style={{ color: "#FF4400", flexShrink: 0, marginTop: 2 }}
                        />
                        <span>{errorMsg}</span>
                    </div>
                ) : null}
                <p className="overline mt-4">Unsubscribe anytime. No spam.</p>
            </div>
        );
    }

    return (
        <div data-testid="newsletter-form-success">
            <div className="flex items-center gap-3 mb-4">
                <span
                    className="inline-flex w-8 h-8 items-center justify-center border"
                    style={{
                        borderColor: "var(--cobalt)",
                        color: "var(--cobalt)",
                        background: "rgba(26,63,204,0.08)",
                    }}
                    data-testid="subscribed-badge"
                >
                    <Check size={14} />
                </span>
                <span
                    className="overline-cobalt"
                    style={{ color: "var(--cobalt)" }}
                >
                    [Step 02] Subscribed
                </span>
            </div>
            <h3
                className="font-display mb-3"
                style={{
                    fontSize: 30,
                    lineHeight: 1.05,
                    color: "var(--ink)",
                    letterSpacing: "-0.025em",
                }}
            >
                You're in. Check your{" "}
                <span
                    style={{
                        fontFamily: "'Fraunces', serif",
                        fontStyle: "italic",
                        fontWeight: 400,
                        color: "var(--cobalt)",
                    }}
                >
                    inbox.
                </span>
            </h3>
            <p
                className="mb-5"
                style={{
                    color: "var(--ink-2)",
                    fontSize: 13.5,
                    lineHeight: 1.6,
                }}
            >
                Your welcome email is on its way. Not seeing it? Check spam or promotions.
            </p>
            <div
                style={{
                    borderTop: "1px solid var(--rule-soft)",
                    margin: "20px 0 16px 0",
                }}
            />
            <h3
                className="font-display mb-3"
                style={{
                    fontSize: 24,
                    lineHeight: 1.05,
                    color: "var(--ink)",
                    letterSpacing: "-0.025em",
                }}
            >
                Accredited{" "}
                <span
                    style={{
                        fontFamily: "'Fraunces', serif",
                        fontStyle: "italic",
                        fontWeight: 400,
                        color: "var(--cobalt)",
                        marginLeft: "0.15em",
                    }}
                >
                    investor?
                </span>
            </h3>
            <p
                className="mb-5"
                style={{
                    color: "var(--ink-2)",
                    fontSize: 13,
                    lineHeight: 1.6,
                }}
            >
                I occasionally share deals on a separate track with its own screening.
            </p>
            {typeformUrl ? (
                <a
                    href={buildCommunityUrl()}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary w-full justify-center"
                    data-testid="community-cta"
                    onClick={() => {
                        // Fire-and-forget: record intent to screen for deals.
                        // Don't await, we don't want to delay the Typeform handoff.
                        Newsletter.communityClick(email).catch(() => {});
                    }}
                >
                    See if you qualify <ArrowRight size={14} />
                </a>
            ) : (
                <div
                    className="panel-sub p-3 text-center"
                    data-testid="community-cta-placeholder"
                >
                    <p
                        style={{
                            color: "var(--ink-2)",
                            fontSize: 12.5,
                            lineHeight: 1.5,
                        }}
                    >
                        Screening opens soon.
                    </p>
                </div>
            )}
            {onClose ? (
                <button
                    type="button"
                    onClick={onClose}
                    className="btn-ghost w-full justify-center mt-3"
                    data-testid="newsletter-form-done"
                >
                    Skip for now
                </button>
            ) : null}
        </div>
    );
}
